import {
  applyModelIdPrefixV820,
  buildModelMappingSubmissionV820,
  getUpstreamModelSuggestionsV820,
  parseModelMappingRowsV820,
} from './channelModelVisibilityV820';

export const MODEL_RULES_SCHEMA_V822 = 'alfredapi.model-rules';
export const MODEL_RULES_MAX_BYTES_V822 = 256 * 1024;
export const MODEL_RULES_MAX_COUNT_V822 = 500;
export const MODEL_RESOLUTIONS_V822 = ['', '512', '1K', '2K', '4K'];

const keyOf = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();
const isRecord = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const validModelName = (value) =>
  typeof value === 'string' &&
  value.trim().length > 0 &&
  value.length <= 256 &&
  Array.from(value).every(
    (character) =>
      character.charCodeAt(0) > 31 &&
      character.charCodeAt(0) !== 127 &&
      character !== ','
  ) &&
  !['__proto__', 'constructor', 'prototype'].includes(keyOf(value));
const onlyKeys = (value, keys) =>
  Object.keys(value).every((key) => keys.includes(key));
const byteLength = (text) => new Blob([text]).size;
const fail = (code) => {
  throw new Error(code);
};

export const parseModelRulesRowsV822 = (
  modelMapping,
  config = {},
  channelModels = [],
  modelIdPrefix = ''
) => {
  const policies = isRecord(config.model_request_policies_v822)
    ? config.model_request_policies_v822
    : Object.create(null);
  return parseModelMappingRowsV820(
    modelMapping,
    config.model_mapping_target_only_models,
    channelModels,
    modelIdPrefix
  )
    .map((row) => ({
      ...row,
      // Older channels declared public aliases in Models. Preserve only their
      // exact saved mapping; new/retargeted rules still require native models.
      ...(channelModels.includes(row.publicModel)
        ? { savedMappingV1015: [row.publicModel, row.upstreamModel] }
        : {}),
      enabled: true,
      resolution: hasOwn(policies, row.publicModel)
        ? policies[row.publicModel]?.resolution ?? ''
        : '',
    }))
    .concat(
      (Array.isArray(config.model_rules_disabled_v107)
        ? config.model_rules_disabled_v107
        : []
      ).map((rule, index) => ({
        id: `mapping-disabled-${index}`,
        upstreamModel: rule.upstream_model,
        publicModel: rule.public_model,
        resolution: rule.resolution || '',
        targetOnly: rule.hide_original,
        enabled: false,
      }))
    );
};

export const updateModelRuleRowV822 = (rows, rowId, field, value) => {
  const current = rows.find((row) => row.id === rowId);
  if (!current) return rows;
  if (field === 'targetOnly') {
    return rows.map((row) =>
      row.id === rowId ||
      (keyOf(current.upstreamModel) &&
        keyOf(row.upstreamModel) === keyOf(current.upstreamModel))
        ? { ...row, targetOnly: Boolean(value) }
        : row
    );
  }
  return rows.map((row) => {
    if (row.id !== rowId) return row;
    if (field === 'enabled') return { ...row, enabled: Boolean(value) };
    if (field === 'upstreamModel') {
      const sibling = rows.find(
        (candidate) =>
          candidate.id !== rowId &&
          keyOf(value) &&
          keyOf(candidate.upstreamModel) === keyOf(value)
      );
      return {
        ...row,
        upstreamModel: value,
        targetOnly: sibling ? Boolean(sibling.targetOnly) : row.targetOnly,
      };
    }
    if (field === 'publicModel' || field === 'resolution') {
      return { ...row, [field]: value };
    }
    return row;
  });
};

export const buildModelRulesSubmissionV822 = (
  rows,
  channelModels,
  modelIdPrefix = ''
) => {
  const rowError = (error, row) => ({
    error,
    rowId: row?.id,
    upstreamModel: row?.upstreamModel,
    publicModel: row?.publicModel,
  });
  const filledRows = rows.filter(
    (row) => row.upstreamModel || row.publicModel || row.resolution
  );
  const publicKeys = new Set();
  for (const row of filledRows) {
    if (!row.upstreamModel?.trim() || !row.publicModel?.trim()) {
      return rowError('incomplete', row);
    }
    if (
      !validModelName(row.upstreamModel) ||
      !validModelName(row.publicModel)
    ) {
      return rowError('invalid_name', row);
    }
    if (!MODEL_RESOLUTIONS_V822.includes(row.resolution ?? '')) {
      return rowError('invalid_resolution', row);
    }
    const publicKey = keyOf(row.publicModel);
    if (publicKeys.has(publicKey))
      return rowError('duplicate_public_name', row);
    publicKeys.add(publicKey);
    if (keyOf(row.upstreamModel) === publicKey)
      return rowError('same_name', row);
  }
  const activeRows = filledRows.filter((row) => row.enabled !== false);
  const nativeModels = getUpstreamModelSuggestionsV820(
    channelModels,
    modelIdPrefix
  );
  const legacyRows = activeRows.filter(
    (row) =>
      row.savedMappingV1015?.[0] === row.publicModel &&
      row.savedMappingV1015?.[1] === row.upstreamModel &&
      channelModels.includes(row.publicModel) &&
      (!row.targetOnly || nativeModels.includes(row.upstreamModel))
  );
  const legacyAliases = new Set(legacyRows.map((row) => row.publicModel));
  const validationModels = [
    ...channelModels.filter((model) => !legacyAliases.has(model)),
    ...legacyRows.map((row) =>
      applyModelIdPrefixV820(row.upstreamModel, modelIdPrefix)
    ),
  ];
  const submission = buildModelMappingSubmissionV820(
    activeRows,
    validationModels,
    modelIdPrefix
  );
  if (submission.error) {
    const row =
      activeRows.find(
        (candidate) =>
          buildModelMappingSubmissionV820(
            [candidate],
            validationModels,
            modelIdPrefix
          ).error
      ) ||
      activeRows.find(
        (candidate) =>
          candidate.targetOnly && publicKeys.has(keyOf(candidate.upstreamModel))
      );
    return rowError(submission.error, row);
  }
  const visibility = new Map();
  const policies = Object.create(null);
  const nativeKeys = new Set(
    getUpstreamModelSuggestionsV820(validationModels, modelIdPrefix).map(keyOf)
  );
  for (const row of activeRows) {
    const upstreamKey = keyOf(row.upstreamModel);
    if (
      visibility.has(upstreamKey) &&
      visibility.get(upstreamKey) !== Boolean(row.targetOnly)
    ) {
      return rowError('visibility_conflict', row);
    }
    visibility.set(upstreamKey, Boolean(row.targetOnly));
    if (nativeKeys.has(keyOf(row.publicModel))) {
      return rowError('native_alias_conflict', row);
    }
    if (row.resolution) {
      policies[row.publicModel.trim()] = { resolution: row.resolution };
    }
  }
  const disabledRules = filledRows
    .filter((row) => row.enabled === false)
    .map((row) => ({
      upstream_model: row.upstreamModel.trim(),
      public_model: row.publicModel.trim(),
      resolution: row.resolution || '',
      hide_original: Boolean(row.targetOnly),
    }));
  return { ...submission, policies, disabledRules };
};

export const parseModelRulesBundleV822 = (text) => {
  if (typeof text !== 'string') fail('invalid_bundle');
  if (byteLength(text) > MODEL_RULES_MAX_BYTES_V822) fail('file_too_large');
  let bundle;
  try {
    bundle = JSON.parse(text);
  } catch {
    fail('invalid_json');
  }
  if (
    !isRecord(bundle) ||
    !onlyKeys(bundle, ['schema', 'version', 'rules']) ||
    bundle.schema !== MODEL_RULES_SCHEMA_V822 ||
    ![1, 2].includes(bundle.version) ||
    !Array.isArray(bundle.rules)
  ) {
    fail('invalid_bundle');
  }
  if (
    !bundle.rules.length ||
    bundle.rules.length > MODEL_RULES_MAX_COUNT_V822
  ) {
    fail('rule_limit');
  }
  return bundle.rules.map((rule, index) => {
    if (
      !isRecord(rule) ||
      !onlyKeys(rule, [
        'upstream_model',
        'public_model',
        'resolution',
        'hide_original',
        ...(bundle.version === 2 ? ['enabled'] : []),
      ]) ||
      !validModelName(rule.upstream_model) ||
      !validModelName(rule.public_model) ||
      typeof rule.hide_original !== 'boolean' ||
      (bundle.version === 2 && typeof rule.enabled !== 'boolean') ||
      (hasOwn(rule, 'resolution') &&
        !MODEL_RESOLUTIONS_V822.includes(rule.resolution))
    ) {
      fail('invalid_rule');
    }
    return {
      id: `import-${index}`,
      upstreamModel: rule.upstream_model.trim(),
      publicModel: rule.public_model.trim(),
      resolution: rule.resolution ?? '',
      targetOnly: rule.hide_original,
      enabled: bundle.version === 1 ? true : rule.enabled,
    };
  });
};

export const exportModelRulesBundleV822 = (
  rows,
  selectedIds,
  channelModels,
  modelIdPrefix = ''
) => {
  const selected = new Set(selectedIds);
  const selectedRows = rows.filter((row) => selected.has(row.id));
  if (
    !selectedRows.length ||
    selectedRows.length > MODEL_RULES_MAX_COUNT_V822
  ) {
    fail('rule_limit');
  }
  const submission = buildModelRulesSubmissionV822(
    selectedRows,
    channelModels,
    modelIdPrefix
  );
  if (submission.error) fail(submission.error);
  const text = JSON.stringify(
    {
      schema: MODEL_RULES_SCHEMA_V822,
      version: selectedRows.some((row) => row.enabled === false) ? 2 : 1,
      rules: selectedRows.map((row) => ({
        upstream_model: row.upstreamModel.trim(),
        public_model: row.publicModel.trim(),
        resolution: row.resolution || '',
        hide_original: Boolean(row.targetOnly),
        ...(selectedRows.some((entry) => entry.enabled === false)
          ? { enabled: row.enabled !== false }
          : {}),
      })),
    },
    null,
    2
  );
  if (byteLength(text) > MODEL_RULES_MAX_BYTES_V822) fail('file_too_large');
  return text;
};

export const planModelRulesImportV822 = (
  importedRows,
  existingRows,
  channelModels,
  modelIdPrefix = '',
  decisions = Object.create(null)
) => {
  const nativeModels = new Set(
    getUpstreamModelSuggestionsV820(channelModels, modelIdPrefix)
  );
  const proposed = existingRows.slice();
  const entries = importedRows.map((rule) => {
    const decision = hasOwn(decisions, rule.id) ? decisions[rule.id] : {};
    const row = {
      id: rule.id,
      upstreamModel: decision.upstreamModel ?? rule.upstreamModel,
      publicModel: decision.publicModel ?? rule.publicModel,
      resolution: rule.resolution,
      targetOnly: decision.targetOnly ?? rule.targetOnly,
      enabled: rule.enabled !== false,
    };
    if (decision.skip) return { row, skipped: true, error: '' };
    if (!nativeModels.has(row.upstreamModel)) {
      return { row, skipped: false, error: 'upstream_not_declared' };
    }
    const result = buildModelRulesSubmissionV822(
      [...proposed, row],
      channelModels,
      modelIdPrefix
    );
    if (!result.error) proposed.push(row);
    return { row, skipped: false, error: result.error };
  });
  const additions = proposed.slice(existingRows.length);
  return {
    entries,
    additions,
    canApply: additions.length > 0 && entries.every((entry) => !entry.error),
  };
};

export const buildModelPreviewDraftV822 = ({
  type,
  models,
  modelIdPrefix,
  rows,
  publicModelIDs,
  modelSyncProvider,
}) => {
  const submission = buildModelRulesSubmissionV822(rows, models, modelIdPrefix);
  if (submission.error) return { ...submission, body: null };
  const safeConfig = {
    model_mapping_target_only_models: submission.targetOnlyModels,
    model_request_policies_v822: submission.policies,
    ...(publicModelIDs ? { public_model_ids_v113: publicModelIDs } : {}),
    ...(modelSyncProvider
      ? { model_sync_v103: { provider: modelSyncProvider } }
      : {}),
    ...(submission.disabledRules.length
      ? { model_rules_disabled_v107: submission.disabledRules }
      : {}),
  };
  return {
    error: '',
    body: {
      type: Number(type),
      models: models.join(','),
      model_mapping: submission.modelMapping,
      model_id_prefix: modelIdPrefix || '',
      config: JSON.stringify(safeConfig),
    },
  };
};

export const modelRuleErrorTextV1015 = (result, t) => {
  const message = t(`channel.edit.model_rules_v822.errors.${result.error}`);
  return result.rowId
    ? t('channel.edit.model_rules_v822.rule_error_v1015', {
        upstream: result.upstreamModel || '?',
        public: result.publicModel || '?',
        message,
      })
    : message;
};

export const parseModelPreviewResponseV822 = (response) => {
  const payload = response?.data;
  const modelIds = payload?.data?.model_ids;
  const policies = payload?.data?.policies;
  if (
    payload?.success !== true ||
    !Array.isArray(modelIds) ||
    !modelIds.every(validModelName) ||
    new Set(modelIds).size !== modelIds.length ||
    !isRecord(policies)
  ) {
    fail('preview_failed');
  }
  const safePolicies = Object.create(null);
  for (const [publicId, policy] of Object.entries(policies)) {
    if (
      !modelIds.includes(publicId) ||
      !isRecord(policy) ||
      !onlyKeys(policy, ['resolution']) ||
      !MODEL_RESOLUTIONS_V822.includes(policy.resolution)
    ) {
      fail('preview_failed');
    }
    safePolicies[publicId] = { resolution: policy.resolution };
  }
  return { modelIds: modelIds.slice(), policies: safePolicies };
};
